import { useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronRight, User } from "lucide-react";
import { StatementDownload, monthsOf } from "@/components/StatementDownload";

const sum = (rows, k = "final_payout") => Math.round(rows.reduce((t, p) => t + (Number(p[k]) || 0), 0) * 100) / 100;
const legacy = (p) => p.labour_amount == null && p.visit_amount == null;
const grossOf = (p) => (legacy(p) ? (Number(p.final_payout) || 0) + (Number(p.commission) || 0) : (Number(p.labour_amount) || 0) + (Number(p.visit_amount) || 0) + (p.materials_v2 ? 0 : Number(p.material_amount) || 0));
const labourCutOf = (p) => (p.labour_commission != null ? Number(p.labour_commission) : (Number(p.commission) || 0) - (Number(p.visit_margin) || 0));
const money = (n) => { const v = Number(n || 0); return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
const statusOf = (p) => (p.status === "Paid" ? "Paid" : "Due");

function totalsOf(rows) {
    return {
        gross: Math.round(rows.reduce((t, p) => t + grossOf(p), 0) * 100) / 100,
        visitMargin: sum(rows, "visit_margin"), labourCut: Math.round(rows.reduce((t, p) => t + labourCutOf(p), 0) * 100) / 100, margin: sum(rows, "commission"),
        net: sum(rows), paid: sum(rows.filter((p) => p.status === "Paid")), due: sum(rows.filter((p) => p.status !== "Paid")),
    };
}

function MarginStrip({ t, testid }) {
    const cells = [["Job earnings", t.gross, "text-navy"], ["Visit margin", -t.visitMargin, "text-rose-600"], ["Labour commission", -t.labourCut, "text-rose-600"],
        ["Net to Pro", t.net, "text-navy"], ["Paid", t.paid, "text-emerald-700"], ["To pay", t.due, "text-amber-700"]];
    return (
        <div data-testid={testid} className="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-navy/10 sm:grid-cols-6">
            {cells.map(([l, v, c]) => (
                <div key={l} className="bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-navy/40">{l}</p>
                    <p className={`font-heading text-sm font-extrabold ${c}`} data-testid={`${testid}-${l.toLowerCase().replace(/\s/g, "-")}`}>{v < 0 ? `−${money(-v)}` : money(v)}</p>
                </div>
            ))}
        </div>
    );
}

function groupByPro(payouts) {
    const map = {};
    (payouts || []).forEach((p) => {
        const k = p.professional_id || "unassigned";
        map[k] = map[k] || { id: k, name: p.professional_name || "Unassigned", pro_id: p.pro_id, rows: [] };
        map[k].rows.push(p);
    });
    return Object.values(map).map((g) => {
        const due = g.rows.filter((p) => p.status !== "Paid");
        return { ...g, due, dueTotal: sum(due), paidTotal: sum(g.rows.filter((p) => p.status === "Paid")), totals: totalsOf(g.rows) };
    }).sort((a, b) => b.dueTotal - a.dueTotal || a.name.localeCompare(b.name));
}

function ProPayoutCard({ g, onMarkPaid, onChanged }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const payAll = async () => {
        setBusy(true);
        try { const { data } = await api.post("/admin/payouts/pay-all", { professional_id: g.id }); toast.success(`${data.count} payout(s) marked paid for ${g.name}`); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="overflow-hidden rounded-2xl border border-navy/10 bg-white" data-testid={`pro-payout-card-${g.pro_id || g.id}`}>
            <div className="flex flex-wrap items-center gap-3 p-4">
                <button data-testid={`pro-payout-toggle-${g.pro_id || g.id}`} onClick={() => setOpen((o) => !o)} className="flex flex-1 items-center gap-3 text-left">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange/10 text-orange"><User size={18} /></span>
                    <span className="flex-1">
                        <span className="block font-heading font-bold text-navy">{g.name}</span>
                        <span className="text-xs text-navy/45">{g.pro_id || "—"} · {g.rows.length} job{g.rows.length === 1 ? "" : "s"}</span>
                    </span>
                    {open ? <ChevronDown size={18} className="text-navy/40" /> : <ChevronRight size={18} className="text-navy/40" />}
                </button>
                <div className="flex items-center gap-4 text-right">
                    <div><p className="text-[10px] font-semibold uppercase tracking-wide text-navy/40">Due</p><p className="font-heading font-extrabold text-amber-700" data-testid={`pro-payout-due-${g.pro_id || g.id}`}>{money(g.dueTotal)}</p></div>
                    <div><p className="text-[10px] font-semibold uppercase tracking-wide text-navy/40">Paid</p><p className="font-heading font-extrabold text-emerald-700">{money(g.paidTotal)}</p></div>
                    {g.due.length > 0 && g.id !== "unassigned" && (
                        <button data-testid={`pro-pay-all-${g.pro_id || g.id}`} disabled={busy} onClick={payAll} className="flex items-center gap-1 rounded-full bg-navy px-3.5 py-2 text-xs font-bold text-white disabled:opacity-60">{busy && <Loader2 size={12} className="animate-spin" />} Pay all due</button>
                    )}
                </div>
            </div>
            <div className="px-4 pb-3"><MarginStrip t={g.totals} testid={`pro-margin-${g.pro_id || g.id}`} /></div>
            {open && g.id !== "unassigned" && (
                <div className="flex items-center justify-between gap-3 border-t border-navy/5 bg-navy/[0.02] px-4 py-2.5">
                    <span className="text-xs font-semibold text-navy/55">Monthly payout statement (PDF)</span>
                    <StatementDownload months={monthsOf(g.rows)} professionalId={g.id} testid={`statement-${g.pro_id || g.id}`} />
                </div>
            )}
            {open && (
                <div className="overflow-x-auto border-t border-navy/5 homiie-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                            <tr><th className="px-4 py-2.5">Booking</th><th className="px-4 py-2.5">Labour</th><th className="px-4 py-2.5">Visit</th><th className="px-4 py-2.5">Job earning</th><th className="px-4 py-2.5">Visit margin</th><th className="px-4 py-2.5">Labour comm.</th><th className="px-4 py-2.5">Net to Pro</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th></tr>
                        </thead>
                        <tbody>
                            {g.rows.map((p) => (
                                <tr key={p.id} className="border-t border-navy/5" data-testid={`payout-row-${p.id}`}>
                                    <td className="px-4 py-2.5 font-heading font-bold text-navy">{p.booking_ref}{p.kind && <span className="ml-1.5 text-[10px] font-semibold uppercase text-navy/40">{p.kind.replace(/_/g, " ")}</span>}</td>
                                    <td className="px-4 py-2.5 text-navy/70">{money(p.labour_amount || 0)}</td>
                                    <td className="px-4 py-2.5 text-navy/70">{money(p.visit_amount || 0)}</td>
                                    <td className="px-4 py-2.5 text-navy/70">{money(grossOf(p))}</td>
                                    <td className="px-4 py-2.5 text-rose-600">−{money(p.visit_margin || 0)}</td>
                                    <td className="px-4 py-2.5 text-rose-600">−{money(labourCutOf(p))}</td>
                                    <td className="px-4 py-2.5 font-bold text-navy" data-testid={`payout-due-${p.id}`}>{money(p.final_payout)}</td>
                                    <td className="px-4 py-2.5"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${p.status === "Paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{statusOf(p)}</span></td>
                                    <td className="px-4 py-2.5">{p.status !== "Paid" && <button data-testid={`mark-payout-${p.id}`} onClick={() => onMarkPaid(p.id)} className="rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white">Mark paid</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function PayoutsByPro({ payouts, onMarkPaid, onChanged }) {
    const groups = useMemo(() => groupByPro(payouts), [payouts]);
    if (payouts === null) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>;
    if (!groups.length) return <div className="mt-4 rounded-2xl border border-navy/10 bg-white py-12 text-center text-sm text-navy/40">No payouts yet.</div>;
    return (
        <div className="mt-4 space-y-3" data-testid="payouts-by-pro">
            <div className="rounded-2xl border border-navy/10 bg-white p-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-navy/50">All professionals · materials are paid to vendors, not in payouts</p>
                <MarginStrip t={totalsOf(payouts)} testid="payout-totals" />
            </div>
            {groups.map((g) => <ProPayoutCard key={g.id} g={g} onMarkPaid={onMarkPaid} onChanged={onChanged} />)}
        </div>
    );
}
